/**
 * Module 35 (WP-35.5) — free 2-axis movement + width-matched gate exit.
 *
 * The new-model movement engine, built on the WP-35.4 shape helpers. It is
 * mechanic-complete and unit-tested here, but NOT yet wired into the live game —
 * the solver (WP-35.6), renderer/input (WP-35.7) and generator (WP-35.8) migrate
 * onto it as Phase 1 lands as a coherent set. Until then the game keeps running on
 * the legacy `move-validator.ts` / `win-detector.ts` (single-axis), so this file
 * changes no current behaviour.
 *
 * Model (clean, reference-matching):
 *   - Movement = `movementConstraint` + in-grid bounds + cell-set collision. Gates
 *     never block in-grid movement; a block simply cannot slide off the grid.
 *   - Exit = a SEPARATE removal: a block whose leading edge has reached a wall,
 *     whose every occupied lane fits a same-colour gate opening (width-matched),
 *     and whose sweep out through that opening is clear, exits. (Module 53
 *     replaced "flush cross-section" here — see `exitsThroughGate`.)
 *   Free blocks move 4-way; arrow-locked blocks keep the single-axis code path.
 *
 * ── Module 53 — the exit geometry, and the moment it is asked about ──────────
 * Two changes, both in this file, both from `Bugs/Rainbow Block and Gate.mp4`:
 * the flushness clause was unsatisfiable for a concave shape (see
 * `exitsThroughGate` and `leadingEdgeAtWall`), and `slideAndExitAgainst` asked
 * about gates only after a step, so a block already against the wall was never
 * asked at all. Each alone was enough to refuse the reported block.
 *
 * ── WP-50.4 — WHEN the exit is checked, and why it is checked here ────────────
 * The two clauses above say what a move is and what an exit is. They do not say
 * how the two COMPOSE, and for a long time each caller answered that for itself:
 * the renderer resolved a drag as *step, ask, repeat*, while the model slid the
 * block all the way and asked once at the end. Both used these helpers, neither
 * added a second implementation of anything, and they still disagreed — a block
 * that merely passes a matching gate left the board in the game and stayed put in
 * the model. That was AUD-010; par differed on 25 of the first 40 levels.
 *
 * Settled 2026-08-12 in favour of the game as it is played: **a block leaves the
 * moment it draws level with a gate it can go through**, so it can never be slid
 * past its own gate. `slideAndExitAgainst` below is the single composition of the
 * two clauses, and every caller that resolves a move goes through it.
 */
import { gateIsOpen } from './power';
import { canMoveInDirection, occupiedCells } from './shape';
/**
 * Would this block leave through some powered gate, if only its plate were
 * covered?
 *
 * Asked by re-running the CANONICAL predicate with `surge: true`, because surge
 * lifts exactly one clause — power — and nothing else. That is the whole trick:
 * no clause is restated here, so this cannot drift from `exitsThroughGate` the
 * way a hand-copied "is it at the wall and does it fit" check would. The module
 * header on AUD-010 is about precisely that failure.
 *
 * Returns `null` when power is not what is refusing: an ordinary gate, a colour
 * mismatch, an axis lock, a wall, or a board already under surge.
 */
export function powerRefusalAt(block, exits, gridSize, others, opts = {}) {
    // Under surge nothing is being refused on power, by definition.
    if (opts.surge)
        return null;
    for (const exit of exits) {
        if (!exit.powered)
            continue;
        // Both halves are needed, and the second alone is not enough: a gate whose
        // plate IS covered also passes the surge query, so asking only that reports
        // a refusal for a gate that is standing wide open. Refusal means it says no
        // NOW and would say yes with power lifted.
        if (exitsThroughGate(block, exit, gridSize, others, opts))
            continue;
        if (exitsThroughGate(block, exit, gridSize, others, { ...opts, surge: true }))
            return { exit, plateId: exit.powered.plateId };
    }
    return null;
}
const DELTA = {
    up: { row: -1, col: 0 },
    down: { row: 1, col: 0 },
    left: { row: 0, col: -1 },
    right: { row: 0, col: 1 },
};
const key = (c) => `${c.row},${c.col}`;
/** Shared empty set, so the common "no plates on this board" path allocates nothing. */
const EMPTY_POWERED = new Set();
/**
 * Module 75 (D4) — the power set with `block` standing where it is: a plate is
 * lit when one of the other blocks (`others`, the same cell-key set every
 * caller already builds) or the mover itself covers it. This is the solver's
 * rule (`plateCovered`: "with the moving block at (row, col)") and the
 * renderer's mid-drag `currentPowered()`, stated once for the model.
 */
export function powerWithMover(block, others, plates) {
    if (!plates?.length)
        return EMPTY_POWERED;
    const mine = new Set(occupiedCells(block).map(key));
    const lit = new Set();
    for (const plate of plates) {
        const at = key(plate.cell);
        if (others.has(at) || mine.has(at))
            lit.add(plate.id);
    }
    return lit;
}
/**
 * Occupied `"row,col"` keys of every block except `exceptId`. Exported so the
 * free-slide solver can build it ONCE per node and reuse it across a block's four
 * slide directions instead of rebuilding it per step.
 */
export function occupiedKeysExcept(blocks, exceptId) {
    const set = new Set();
    for (const b of blocks) {
        if (b.id === exceptId)
            continue;
        for (const c of occupiedCells(b))
            set.add(key(c));
    }
    return set;
}
/** Can the rigid cell-set step one `delta` staying in-grid and clear of `others`? */
function canStepCells(cells, delta, others, gridSize) {
    const own = new Set(cells.map(key));
    for (const c of cells) {
        const next = { row: c.row + delta.row, col: c.col + delta.col };
        // Sliding over the block's own vacated cells is fine.
        if (own.has(key(next)))
            continue;
        // Grid boundary — exiting is a separate step, never a normal move.
        if (next.row < 0 || next.row >= gridSize || next.col < 0 || next.col >= gridSize)
            return false;
        // Collision with another block.
        if (others.has(key(next)))
            return false;
    }
    return true;
}
/**
 * Can `block` slide exactly one cell in `direction`? Any shape; honours the
 * movement constraint (free = 4-way, arrow-locked = single axis); stops at the
 * grid boundary; collides on cell-sets. Gates are irrelevant to movement.
 */
export function canStep(block, direction, allBlocks, gridSize) {
    if (!canMoveInDirection(block, direction))
        return false;
    return canStepCells(occupiedCells(block), DELTA[direction], occupiedKeysExcept(allBlocks, block.id), gridSize);
}
/** Move a block one cell (no validation) — caller must `canStep` first. */
export function step(block, direction) {
    const delta = DELTA[direction];
    return {
        ...block,
        position: { row: block.position.row + delta.row, col: block.position.col + delta.col },
    };
}
/**
 * Slide `block` as far as it can in `direction` against a PRECOMPUTED set of the
 * other blocks' occupied cells, **stopping the instant it lines up with a gate it
 * can leave through**. The efficient core the solver drives per node;
 * `slideAndExit` is the convenience wrapper that builds `others` for you.
 *
 * This is the ONE place a slide is composed with an exit check (WP-50.4). Pass no
 * exits and it degrades to pure movement — which is exactly what `slideFullyAgainst`
 * does, so there is a single stepping loop in the codebase rather than one per
 * caller. See the module header for why the granularity is the whole point.
 */
export function slideAndExitAgainst(block, direction, others, exits, gridSize, opts = {}, plates) {
    if (!canMoveInDirection(block, direction))
        return null;
    // Module 75 (D4) — with `plates`, power is judged at every position asked,
    // the mover counted where it stands, instead of the fixed
    // `opts.poweredPlateIds` a caller computed before the move. Without them the
    // behaviour is exactly as before, which is what the generator and the
    // difficulty score rely on.
    const at = (b) => plates ? { ...opts, poweredPlateIds: powerWithMover(b, others, plates) } : opts;
    // Module 53 — asked BEFORE the first step, for a gate on the dragged
    // direction's own wall.
    //
    // Without this the reported block cannot leave however correct the predicate
    // is, and the two defects hid each other. Measured on the real level 128
    // (`Bugs/Rainbow Block and Gate.mp4`): the green L sits at rows 1–2 with a cell
    // already at column 0, so `canStepCells` refuses the first left step — the
    // block is against the wall — the loop never runs, and the exit question is
    // never asked. Fixing only the geometry would have left the recording
    // reproducing exactly as filed.
    //
    // It cannot normally arise, which is why it went unnoticed: a block that
    // reaches an exiting position leaves at once, and the generator rejects any
    // board that starts in one. What creates it is a gate that becomes usable
    // while the block is already parked against it — i.e. **Rainbow**, which is
    // why the only two recordings of this are both Rainbow recordings.
    //
    // Deliberately narrowed to `e.direction === direction` rather than any gate.
    // The post-step check asks about every gate, which is right for a block in
    // motion; before a step there has been no gesture except the drag itself, and
    // pulling a block towards the wall it is already against is the only reading
    // of that gesture that removes it.
    const atRest = exits.find((e) => e.direction === direction && exitsThroughGate(block, e, gridSize, others, at(block)));
    if (atRest)
        return { moved: block, exited: atRest };
    const delta = DELTA[direction];
    let cells = occupiedCells(block);
    const anchor = { ...block.position };
    let moved = false;
    while (canStepCells(cells, delta, others, gridSize)) {
        cells = cells.map((c) => ({ row: c.row + delta.row, col: c.col + delta.col }));
        anchor.row += delta.row;
        anchor.col += delta.col;
        moved = true;
        // Asked after EVERY step, never once at the end of the travel: a block that
        // merely draws level with a matching gate on its way past leaves there and
        // then, and the cells beyond are never reached.
        const here = { ...block, position: { ...anchor } };
        const gate = exitingGate(here, exits, gridSize, others, at(here));
        if (gate)
            return { moved: here, exited: gate };
    }
    if (!moved)
        return null;
    // The no-movement case above still returns `null`, deliberately: the generator
    // and `difficulty-score.ts` both read a non-null outcome as "it moved", and
    // handing them a stationary one would corrupt both. A block that cannot move
    // at all is asked about with `powerRefusalAt` directly by the renderer.
    const rested = { ...block, position: anchor };
    const refusedBy = powerRefusalAt(rested, exits, gridSize, others, at(rested));
    return refusedBy ? { moved: rested, exited: null, refusedBy } : { moved: rested, exited: null };
}
/** No gates — the argument that turns `slideAndExitAgainst` into pure movement. */
const NO_EXITS = [];
/**
 * Slide `block` as far as it can in `direction` against a PRECOMPUTED set of the
 * other blocks' occupied cells. **Movement only** — gates are not consulted, so
 * this answers "how far can it physically travel", not "where does this move end".
 * A caller resolving a player's move wants `slideAndExitAgainst`.
 */
export function slideFullyAgainst(block, direction, others, gridSize) {
    return slideAndExitAgainst(block, direction, others, NO_EXITS, gridSize)?.moved ?? null;
}
/**
 * Slide a block as far as it can go in `direction` (until blocked by a wall or
 * another block), ignoring gates. Returns the moved block, or `null` if it can't
 * move at all. See `slideAndExitAgainst` for the movement-plus-exit resolution
 * that a real move uses.
 */
export function slideFully(block, direction, allBlocks, gridSize) {
    return slideFullyAgainst(block, direction, occupiedKeysExcept(allBlocks, block.id), gridSize);
}
/**
 * Resolve one whole single-axis move: slide until a wall, another block, or a
 * gate this block can leave through — whichever comes first. `null` when the
 * block cannot move that way at all.
 */
export function slideAndExit(block, direction, allBlocks, exits, gridSize, opts = {}) {
    return slideAndExitAgainst(block, direction, occupiedKeysExcept(allBlocks, block.id), exits, gridSize, opts);
}
// ─── Width-matched gate exit ─────────────────────────────────────────────────
/** The wall coordinate a block's facing edge must reach to exit in `direction`. */
function wallCoord(direction, gridSize) {
    return direction === 'right' || direction === 'down' ? gridSize - 1 : 0;
}
/** For a gate on `direction`'s wall, the set of lanes (rows or cols) it spans. */
function gateLanes(exit) {
    const span = exit.span ?? 1;
    const horizontal = exit.direction === 'left' || exit.direction === 'right';
    const start = horizontal ? exit.position.row : exit.position.col;
    const lanes = new Set();
    for (let i = 0; i < span; i++)
        lanes.add(start + i);
    return lanes;
}
const laneOf = (c, direction) => direction === 'left' || direction === 'right' ? c.row : c.col;
const travelOf = (c, direction) => direction === 'left' || direction === 'right' ? c.col : c.row;
/**
 * Does `block` exit through `exit` right now? True when the block may TRAVEL in
 * the gate's direction, the colour matches (or `rainbow`), its leading edge has
 * reached that wall, every lane it occupies falls within the gate opening
 * (width-match), and nothing stands between it and the opening. A gate wider
 * than the block is fine; a gate narrower, or misaligned, is not.
 *
 * ## The geometry clauses (Module 53)
 *
 * Three named clauses below — {@link leadingEdgeAtWall}, {@link lanesFit},
 * {@link pathClear} — replacing the single `facing.every(... === wall ...)` that
 * shipped from Module 35 to Module 52. The old form was **unsatisfiable for a
 * concave shape** and therefore made two of the L's four directions permanently
 * dead, in ordinary play as well as under Rainbow. The clause docs carry the
 * measurements; the reported case is `Bugs/Rainbow Block and Gate.mp4`.
 *
 * `others` is the reason the signature changed. Clearance cannot be evaluated
 * without knowing what else is on the board, and without clearance the
 * relaxation would let a concave block sweep its notch straight through a
 * neighbour — which is the one thing the brief rules out by name. Passing an
 * empty set is therefore not a harmless default but a silent restoration of that
 * bug, and it is why the parameter is required rather than optional.
 *
 * ## The axis clause (Module 52, WP-52.4)
 *
 * A block leaves the board by CONTINUING through the gate, so a gate it cannot
 * travel towards is a gate it cannot leave through. That is physics rather than
 * a rule, and it is the clause this predicate was missing: it checked colour,
 * flushness and width, and never asked `canMoveInDirection`.
 *
 * Free blocks are unaffected — they may travel every way — which is why this is
 * a refinement and not a redesign. What it changes is arrow-locked blocks, and
 * the reported symptom (`Bugs/Arrow Block wrong move in Rainbow.mp4`) was under
 * **Rainbow**, for a reason worth stating: in ordinary play the colour clause
 * hides the gap most of the time, and Rainbow Gates removes the colour clause by
 * design. Under it, every block flush against any wall could leave through any
 * gate that fit — including one on the axis its own arrows forbid. Probed before
 * the fix: a `verticalOnly` block at a LEFT gate returned that gate.
 *
 * ## The power clause (Module 63)
 *
 * A gate carrying `powered` is CLOSED unless its plate is covered — a hold, not
 * a latch, recomputed from block positions on every query rather than stored.
 *
 * It sits beside the colour clause and is independent of it, which is the whole
 * of decision 6: axis ← arrows, colour ← Rainbow, power ← Surge, geometry ←
 * never. Rainbow must NOT open a closed gate; it removes the colour clause and
 * that is all it has ever removed. Collapsing the two would make Rainbow quietly
 * worth double at its existing price, and would leave `exitsThroughGate` with a
 * clause no power-up can reason about independently.
 *
 * ## Why the generator already agreed
 *
 * `free-generator.ts` keeps a block's lock only when the gate it was built around
 * is on that lock's axis (`axisFits`). So the boards have always been CONSTRUCTED
 * on this rule; only the model and the solver did not enforce it, which let a
 * locked block leave through some other same-colour gate by coincidence. This
 * aligns the three rather than introducing a fourth opinion — see the module
 * header on why four callers composing the same primitives differently is
 * exactly how AUD-010 happened.
 */
export function exitsThroughGate(block, exit, gridSize, others, opts = {}) {
    // First, and deliberately before the colour check: this clause is the one that
    // holds when `rainbow` has switched the colour clause off.
    if (!canMoveInDirection(block, exit.direction))
        return false;
    const colourOk = opts.rainbow || exit.colorId === undefined || exit.colorId === block.colorId;
    if (!colourOk)
        return false;
    // Beside colour, never folded into it: Rainbow lifts colour, Surge lifts
    // power. A gate with no `powered` field is always open, so this is inert on
    // every board built before Module 63.
    if (!gateIsOpen(exit, opts.poweredPlateIds ?? EMPTY_POWERED, opts.surge))
        return false;
    const dir = exit.direction;
    return (leadingEdgeAtWall(block, dir, gridSize) &&
        lanesFit(block, exit) &&
        pathClear(block, dir, gridSize, others));
}
/**
 * Is any part of the block against the wall it would leave through?
 *
 * Module 53 replaced `facing.every(cell => travelOf(cell) === wall)` with this.
 * The old form asked every LANE's leading cell to sit at the wall simultaneously,
 * which a rigid concave shape can never do: the L's notch holds one lane's cell
 * one step back, permanently. Measured before the change, over every position on
 * the board and a gate spanning the whole wall, each of the L's four rotations
 * could exit in exactly **two** of its four directions and the other two were
 * unsatisfiable — not merely unsatisfied. `Bugs/Rainbow Block and Gate.mp4` is
 * the `[.# / ##]` rotation at a left gate, one of its two dead directions.
 *
 * The block leaves by continuing to travel, so what matters is that it has
 * ARRIVED — its leading edge is at the wall — not that its trailing geometry is
 * square. The notch follows through the opening a step later.
 */
function leadingEdgeAtWall(block, dir, gridSize) {
    const wall = wallCoord(dir, gridSize);
    return occupiedCells(block).some((cell) => travelOf(cell, dir) === wall);
}
/**
 * Does every lane the block occupies fall inside the gate opening?
 *
 * Unchanged in effect from the old rule — `facingEdgeCells` returns exactly one
 * cell per occupied lane, so testing the facing cells and testing every cell
 * cover the same lane set. Written over all cells because that is what the
 * sentence means, and because the equivalence is a property of
 * `facingEdgeCells`, not something this clause should depend on.
 *
 * A gate wider than the block is fine; narrower or misaligned is not.
 */
function lanesFit(block, exit) {
    const lanes = gateLanes(exit);
    return occupiedCells(block).every((cell) => lanes.has(laneOf(cell, exit.direction)));
}
/**
 * Can the block sweep out through the opening without passing through anything?
 *
 * The clause the relaxation above makes necessary, and it is not decoration.
 * `leadingEdgeAtWall` alone would let the L leave with its notch lane occupied —
 * the notch cell would travel straight through whatever sits between it and the
 * wall. The old strict rule prevented that by accident, and the brief forbids it
 * outright: *"the fix does not allow blocks to pass through gates that they
 * cannot geometrically fit through."*
 *
 * **Vacuous for a rectangle**, which is why relaxing the rule cannot change one.
 * A rectangle's cells fill a contiguous run in every lane, so once its leading
 * edge is at the wall every cell between any of its cells and the wall is one of
 * its own — and `others` never contains the block's own cells.
 */
function pathClear(block, dir, gridSize, others) {
    const delta = DELTA[dir];
    const inGrid = (c) => c.row >= 0 && c.col >= 0 && c.row < gridSize && c.col < gridSize;
    for (const cell of occupiedCells(block)) {
        // Walk from this cell towards the wall. The loop terminates by construction:
        // every step moves one closer to an edge, and it stops on leaving the board,
        // which is where the block is going anyway.
        let probe = { row: cell.row + delta.row, col: cell.col + delta.col };
        while (inGrid(probe)) {
            if (others.has(key(probe)))
                return false;
            probe = { row: probe.row + delta.row, col: probe.col + delta.col };
        }
    }
    return true;
}
/**
 * The gate `block` exits through this instant, or `null`.
 *
 * `others` must be the occupied cells of every OTHER block — build it with
 * {@link occupiedKeysExcept}. It is required rather than defaulted because an
 * empty set silently disables the clearance clause and restores the
 * pass-through bug, with every existing test still green (Module 53).
 */
export function exitingGate(block, exits, gridSize, others, opts = {}) {
    // Module 90 (D3) — Wild Gates is charged only on an exit that NEEDED it. A
    // block that fits both its own-colour gate and another one must be reported
    // leaving through its own, or the player pays for an exit they would have
    // made anyway just because of the order the gates are listed in. Whether the
    // block leaves is unchanged; only which gate it is said to leave by.
    if (opts.rainbow) {
        const own = exits.find((e) => exitsThroughGate(block, e, gridSize, others, { ...opts, rainbow: false }));
        if (own)
            return own;
    }
    return exits.find((e) => exitsThroughGate(block, e, gridSize, others, opts)) ?? null;
}
/**
 * Width-matched WinDetector: the blocks currently sitting at a matching gate.
 *
 * Each block is asked against the others' cells, rebuilt per block — the set is
 * different for each one, so it cannot be hoisted out of the loop.
 */
export function findExitedFree(blocks, exits, gridSize, opts = {}) {
    return blocks.filter((b) => exitingGate(b, exits, gridSize, occupiedKeysExcept(blocks, b.id), opts) !== null);
}
